/**
 * Dev probe: inspect what the REAL backend sends (socket + REST delta/create).
 * Enable in browser console:
 *   localStorage.setItem('__ULTRA_BACKEND_PROBE', '1'); location.reload();
 * Or: window.__ULTRA_BACKEND_PROBE = true; location.reload();
 *
 * Then:
 *   __ultraBackendProbe.printReport()
 *   __ultraBackendProbe.runDeltaProbe()
 *   __ultraBackendProbe.help()
 */

import { fetchReservationsDelta } from "../../store/reservationsSlice";
import { getAllReservations } from "../../api/reservationsService";
import {
   extractSocketDeleteIds,
   extractSocketMutationRows,
   inferSocketMutationOp,
} from "../socket/socketMutationUtils";
import { reservationIdFromAny } from "../ultra/reservationRowUtils";
import { ultraSessionLog, isUltraSessionDebugEnabled, isUltraSessionVerbose } from "./ultraSessionDebug";

const MAX_EVENTS = 200;

function nowIso() {
   return new Date().toISOString();
}

export function isUltraBackendProbeEnabled() {
   if (typeof window === "undefined") return false;
   if (window.__ULTRA_BACKEND_PROBE === true) return true;
   try {
      return localStorage.getItem("__ULTRA_BACKEND_PROBE") === "1";
   } catch {
      return false;
   }
}

function getStartRaw(row) {
   if (!row || typeof row !== "object") return "";
   return String(
      row.startTime ??
         row.start ??
         row.startDate ??
         row.dateTime ??
         row.date ??
         row.reservation?.startTime ??
         "",
   ).trim();
}

function analyzeRowCompleteness(row) {
   const id = reservationIdFromAny(row);
   const start = getStartRaw(row);
   const instructorId =
      row?.instructorId ??
      row?.instructor_id ??
      row?.instructor?.id ??
      row?.reservation?.instructorId ??
      null;
   const userId =
      row?.userId ?? row?.user_id ?? row?.user?.id ?? row?.reservation?.userId ?? null;
   const missing = [];
   if (!id) missing.push("id");
   if (!start) missing.push("startTime");
   if (instructorId == null) missing.push("instructorId");
   if (userId == null) missing.push("userId");
   return {
      id: id || null,
      hasStart: !!start,
      hasInstructor: instructorId != null,
      hasUser: userId != null,
      complete: missing.length === 0,
      missing,
   };
}

function analyzeDeltaPayload(raw) {
   const payload = Array.isArray(raw) ? { items: raw } : raw || {};
   const items = Array.isArray(payload.items)
      ? payload.items
      : Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload.results)
          ? payload.results
          : Array.isArray(payload.reservations)
            ? payload.reservations
            : Array.isArray(payload.list)
              ? payload.list
              : [];
   const deleted = Array.isArray(payload.deleted)
      ? payload.deleted
      : Array.isArray(payload.deletedIds)
        ? payload.deletedIds
        : Array.isArray(payload.removed)
          ? payload.removed
          : Array.isArray(payload.tombstones)
            ? payload.tombstones
            : [];
   const serverTime = payload.serverTime ?? payload.now ?? null;
   const etag = payload.etag ?? null;

   const incompleteItems = items
      .map((row) => analyzeRowCompleteness(row))
      .filter((meta) => !meta.complete);

   const issues = [];
   if (!serverTime) issues.push("NO_SERVER_TIME");
   if (!deleted.length && items.length > 0) {
      issues.push("NO_DELETED_ARRAY (ok if nothing deleted since last sync)");
   }
   if (incompleteItems.length) issues.push(`INCOMPLETE_ITEMS:${incompleteItems.length}`);
   if (!items.length && !deleted.length) issues.push("EMPTY_DELTA");

   return {
      itemCount: items.length,
      deletedCount: deleted.length,
      hasServerTime: !!serverTime,
      hasEtag: !!etag,
      serverTime,
      etag,
      incompleteItems: incompleteItems.slice(0, 5),
      issues,
      topLevelKeys: Object.keys(payload).sort(),
   };
}

function pushRing(buffer, entry, max = MAX_EVENTS) {
   buffer.push({ ts: nowIso(), ...entry });
   if (buffer.length > max) buffer.splice(0, buffer.length - max);
}

function ensureState() {
   if (typeof window === "undefined") return null;
   if (!window.__ultraBackendProbeState) {
      window.__ultraBackendProbeState = {
         socketEvents: [],
         deltaRuns: [],
         createNotes: [],
      };
   }
   return window.__ultraBackendProbeState;
}

export function recordSocketMutationForProbe(eventName, payload) {
   const probeOn = isUltraBackendProbeEnabled();
   const sessionOn = isUltraSessionDebugEnabled();
   if (!probeOn && !sessionOn) return;
   const state = ensureState();
   if (!state) return;

   const op = inferSocketMutationOp(eventName, payload);
   const rows = extractSocketMutationRows(payload);
   const deleteIds = extractSocketDeleteIds(eventName, payload);
   const rowMeta = rows.map((row) => analyzeRowCompleteness(row));

   const issues = [];
   if (op === "unknown") issues.push("UNKNOWN_OP");
   if ((op === "create" || op === "update") && !rows.length) {
      issues.push("THIN_PAYLOAD_NO_ROWS");
   }
   if (op === "delete" && !deleteIds.length) issues.push("DELETE_WITHOUT_IDS");
   if (rowMeta.some((meta) => !meta.complete)) {
      issues.push("INCOMPLETE_ROW → frontend triggers hard month refetch");
   }

   const entry = {
      eventName: String(eventName || ""),
      inferredOp: op,
      rowCount: rows.length,
      deleteIds,
      payloadKeys: payload && typeof payload === "object" ? Object.keys(payload).sort() : [],
      rowMeta,
      issues,
   };

   pushRing(state.socketEvents, entry);

   ultraSessionLog("SOCKET", `rx:${entry.inferredOp}`, {
      eventName: entry.eventName,
      rowCount: entry.rowCount,
      deleteIds: entry.deleteIds,
      issues: entry.issues,
   });

   if (!probeOn && !isUltraSessionVerbose()) return;
   if (!probeOn && entry.issues.length === 0) return;

   // eslint-disable-next-line no-console
   console.log(
      `%c[PROBE:SOCKET] ${entry.inferredOp} ${entry.eventName}`,
      entry.issues.length ? "color:#c0392b;font-weight:bold" : "color:#27ae60",
      entry,
   );
}

export function recordDeltaForProbe(label, rawPayload, analysis) {
   if (!isUltraBackendProbeEnabled()) return;
   const state = ensureState();
   if (!state) return;

   const entry = { label, analysis };
   pushRing(state.deltaRuns, entry, 50);

   // eslint-disable-next-line no-console
   console.log(
      `%c[PROBE:DELTA] ${label}`,
      analysis.issues.some((i) => i.startsWith("NO_SERVER") || i.startsWith("INCOMPLETE"))
         ? "color:#e67e22;font-weight:bold"
         : "color:#2980b9",
      analysis,
   );
}

function buildSocketSummary(events) {
   const byOp = {};
   const issueCounts = {};
   for (const ev of events) {
      byOp[ev.inferredOp] = (byOp[ev.inferredOp] || 0) + 1;
      for (const issue of ev.issues || []) {
         issueCounts[issue] = (issueCounts[issue] || 0) + 1;
      }
   }
   return { total: events.length, byOp, issueCounts };
}

function printRecommendations(socketSummary, lastDelta) {
   const recs = [];

   if ((socketSummary.issueCounts.THIN_PAYLOAD_NO_ROWS || 0) > 0) {
      recs.push({
         priority: "HIGH",
         backend: "WebSocket",
         fix: "Emit full reservation row on create/update: { reservations: [{ id, startTime, instructorId, userId, ... }] }",
         why: "Thin events force expensive month invalidate + delta refetch → flicker",
      });
   }
   if ((socketSummary.issueCounts.DELETE_WITHOUT_IDS || 0) > 0) {
      recs.push({
         priority: "HIGH",
         backend: "WebSocket",
         fix: "On delete emit { id } or { reservations: [{ id }] } or event name containing delete/removed",
         why: "Frontend cannot remove card optimistically; waits for refetch",
      });
   }
   if ((socketSummary.issueCounts["INCOMPLETE_ROW → frontend triggers hard month refetch"] || 0) > 0) {
      recs.push({
         priority: "MEDIUM",
         backend: "WebSocket",
         fix: "Include startTime (ISO) + instructorId on every mutation payload",
         why: "Incomplete rows trigger needsHardDelta → full month reload",
      });
   }
   if (lastDelta && !lastDelta.hasServerTime) {
      recs.push({
         priority: "HIGH",
         backend: "REST /reservations/all",
         fix: "Return serverTime (ISO) on every delta response",
         why: "Delta anchor drifts → missed updates or duplicate refetches in long sessions",
      });
   }
   if (lastDelta && lastDelta.deletedCount === 0 && lastDelta.itemCount > 20) {
      recs.push({
         priority: "MEDIUM",
         backend: "REST /reservations/all",
         fix: "Return deleted: [id, ...] for rows removed since updated_since",
         why: "Without deleted[], removals rely only on socket or full month refetch",
      });
   }

   recs.push({
      priority: "INFO",
      backend: "This repo",
      fix: "Backend is NOT in this repository — only dev.instruireauto.site (separate project)",
      why: "Frontend changes cannot fix server payload shape; probe shows what server actually sends",
   });

   return recs;
}

function installGlobalApi() {
   if (typeof window === "undefined") return;

   window.__ultraBackendProbe = {
      help() {
         // eslint-disable-next-line no-console
         console.log(`
Ultra backend probe
-------------------
Enable:  localStorage.setItem('__ULTRA_BACKEND_PROBE','1'); location.reload();
Disable: localStorage.removeItem('__ULTRA_BACKEND_PROBE'); location.reload();

Commands:
  __ultraBackendProbe.printReport()   — summary socket + delta issues
  __ultraBackendProbe.runDeltaProbe() — fetch delta now (needs Redux store)
  __ultraBackendProbe.runRawDelta()   — raw GET /reservations/all (needs token cookie)
  __ultraBackendProbe.socketLog()     — last socket events table
  __ultraBackendProbe.clear()         — reset captured events

Workflow: enable probe → create/edit/delete reservations → printReport()
`);
      },

      clear() {
         window.__ultraBackendProbeState = {
            socketEvents: [],
            deltaRuns: [],
            createNotes: [],
         };
      },

      socketLog(limit = 30) {
         const events = ensureState()?.socketEvents || [];
         // eslint-disable-next-line no-console
         console.table(
            events.slice(-limit).map((ev) => ({
               ts: ev.ts,
               op: ev.inferredOp,
               event: ev.eventName,
               rows: ev.rowCount,
               deletes: (ev.deleteIds || []).join(","),
               issues: (ev.issues || []).join(" | "),
            })),
         );
         if (typeof window !== "undefined" && Array.isArray(window.__WS_TRACE)) {
            // eslint-disable-next-line no-console
            console.log("Raw WS trace (last 20):", window.__WS_TRACE.slice(-20));
         }
      },

      printReport() {
         const state = ensureState();
         const socketSummary = buildSocketSummary(state?.socketEvents || []);
         const lastDelta = state?.deltaRuns?.[state.deltaRuns.length - 1]?.analysis || null;
         const recs = printRecommendations(socketSummary, lastDelta);

         // eslint-disable-next-line no-console
         console.group("%c[PROBE] Backend compatibility report", "font-size:14px;font-weight:bold");
         // eslint-disable-next-line no-console
         console.log("Socket events captured:", socketSummary.total);
         // eslint-disable-next-line no-console
         console.table(socketSummary.byOp);
         // eslint-disable-next-line no-console
         console.log("Socket issues:", socketSummary.issueCounts);
         if (lastDelta) {
            // eslint-disable-next-line no-console
            console.log("Last delta analysis:", lastDelta);
         } else {
            // eslint-disable-next-line no-console
            console.warn("No delta captured yet — run __ultraBackendProbe.runDeltaProbe()");
         }
         // eslint-disable-next-line no-console
         console.table(recs);
         // eslint-disable-next-line no-console
         console.groupEnd();
         return { socketSummary, lastDelta, recommendations: recs };
      },

      async runDeltaProbe() {
         const store = window.__REDUX_STORE__;
         if (!store?.dispatch || !store?.getState) {
            // eslint-disable-next-line no-console
            console.error("Redux store not exposed. Open calendar as admin/manager first.");
            return null;
         }
         const action = await store.dispatch(fetchReservationsDelta());
         const payload = action?.payload || null;
         const analysis = analyzeDeltaPayload(payload);
         recordDeltaForProbe("fetchReservationsDelta", payload, analysis);
         return { payload, analysis };
      },

      async runRawDelta() {
         const st = window.__REDUX_STORE__?.getState?.();
         const since = st?.reservations?.lastSyncedAt || null;
         const raw = await getAllReservations({
            scope: "all",
            updated_since: since,
            pageSize: 100,
         });
         const analysis = analyzeDeltaPayload(raw);
         recordDeltaForProbe("getAllReservations raw", raw, analysis);
         return { raw, analysis };
      },
   };

   if (isUltraBackendProbeEnabled() && !window.__ULTRA_PROBE_BANNER_SHOWN) {
      window.__ULTRA_PROBE_BANNER_SHOWN = true;
      // eslint-disable-next-line no-console
      console.info(
         "%c[PROBE] Ultra backend probe ON — __ultraBackendProbe.help()",
         "color:#8e44ad;font-weight:bold",
      );
   }
}

installGlobalApi();

export function bootstrapUltraBackendProbe(reduxStore) {
   if (typeof window === "undefined") return;
   if (reduxStore) window.__REDUX_STORE__ = reduxStore;
   installGlobalApi();
}
